import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search } from 'lucide-react';
import { createMepSection, updateMepSection, MepSection, MepCustomItem } from '../../../services/mep/mepService';
import { fetchRecipesByRestaurant, Recipe, TranslatedText, SupportedLanguage, ALL_LANGUAGES, translateTexts } from '../../../services/recipes/recipesService';
import { cn } from '../../../lib/utils';
import { secondaryText, smallTextSize, semiBoldText, primaryText, borderPrimary } from '../../../styling/stylingHelper';
import LanguageTabBar from '../../recipes/LanguageTabBar';

interface SaveMepSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  restaurantId: string;
  section?: MepSection;
  onSave: () => Promise<void>;
}

export default function SaveMepSectionModal({
  isOpen,
  onClose,
  mode,
  restaurantId,
  section,
  onSave,
}: SaveMepSectionModalProps) {
  const { t, i18n } = useTranslation();
  const defaultLang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;

  const [sectionNameTranslations, setSectionNameTranslations] = useState<TranslatedText>({ en: '' });
  const [activeLanguage, setActiveLanguage] = useState<SupportedLanguage>(defaultLang);
  const [isTranslating, setIsTranslating] = useState(false);
  const [selectedMenuItems, setSelectedMenuItems] = useState<string[]>([]);
  const [customItems, setCustomItems] = useState<MepCustomItem[]>([]);
  const [customItemInput, setCustomItemInput] = useState('');
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Filter recipes that have menuItemName set
  const availableRecipes = recipes.filter((recipe) => recipe.menuItemName);
  const uniqueMenuItems = Array.from(
    new Set(availableRecipes.map((r) => r.menuItemName!).filter(Boolean))
  );

  // Filter menu items by search query
  const filteredMenuItems = uniqueMenuItems.filter((item) =>
    item.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const loadRecipes = useCallback(async () => {
    try {
      setLoadingRecipes(true);
      const fetchedRecipes = await fetchRecipesByRestaurant(restaurantId);
      setRecipes(fetchedRecipes);
    } catch (err) {
      console.error('Failed to load recipes:', err);
      setError('Failed to load recipes');
    } finally {
      setLoadingRecipes(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && section) {
        setSectionNameTranslations(section.sectionNameTranslations || { en: section.sectionName });
        setSelectedMenuItems(section.menuItems || []);
        setCustomItems(section.customItems || []);
      } else {
        setSectionNameTranslations({ en: '' });
        setSelectedMenuItems([]);
        setCustomItems([]);
      }
      setCustomItemInput('');
      setActiveLanguage(defaultLang);
      setSearchQuery('');
      setError(null);
      setSaving(false);
      setIsTranslating(false);

      // Load recipes
      loadRecipes();
    }
  }, [isOpen, mode, section, restaurantId, loadRecipes, defaultLang]);

  const handleToggleMenuItem = (menuItem: string) => {
    setSelectedMenuItems((prev) => {
      if (prev.includes(menuItem)) {
        return prev.filter((item) => item !== menuItem);
      } else {
        return [...prev, menuItem];
      }
    });
  };

  const handleAddCustomItem = () => {
    const text = customItemInput.trim();
    if (!text) return;
    setCustomItems((prev) => [...prev, { id: `custom-${crypto.randomUUID()}`, text }]);
    setCustomItemInput('');
  };

  const handleRemoveCustomItem = (id: string) => {
    setCustomItems((prev) => prev.filter((ci) => ci.id !== id));
  };

  const handleTranslate = async () => {
    const currentText = sectionNameTranslations[activeLanguage];
    if (!currentText?.trim()) return;

    setIsTranslating(true);
    try {
      const targetLangs = ALL_LANGUAGES.filter((l) => l !== activeLanguage);
      const result = await translateTexts(
        [{ key: 'sectionName', value: currentText.trim() }],
        activeLanguage,
        targetLangs
      );
      const translated = result.translations['sectionName'];
      if (translated) {
        setSectionNameTranslations((prev) => ({
          ...prev,
          ...Object.fromEntries(targetLangs.map((l) => [l, translated[l] || prev[l] || ''])),
        }));
      }
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSave = async () => {
    // Ensure .en is filled (fallback from active language)
    const finalTranslations = { ...sectionNameTranslations };
    if (!finalTranslations.en?.trim() && finalTranslations[activeLanguage]?.trim()) {
      finalTranslations.en = finalTranslations[activeLanguage]!;
    }

    if (!finalTranslations.en?.trim()) {
      setError(t('mep.nameRequired') || 'Section name is required');
      return;
    }

    if (selectedMenuItems.length === 0 && customItems.length === 0) {
      setError(t('mep.atLeastOneItem') || 'Select at least one menu item or add a custom item');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (mode === 'create') {
        await createMepSection({
          restaurantId,
          sectionName: finalTranslations.en.trim(),
          sectionNameTranslations: finalTranslations,
          menuItems: selectedMenuItems,
          customItems,
        });
      } else if (section) {
        await updateMepSection({
          id: section.id,
          sectionName: finalTranslations.en.trim(),
          sectionNameTranslations: finalTranslations,
          menuItems: selectedMenuItems,
          customItems,
        });
      }
      await onSave();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to save MEP section');
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-2xl border border-zinc-200 dark:border-white/20 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('mep.createMepSection') : t('mep.editMepSection')}
            </h2>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Language Tabs */}
            <LanguageTabBar
              activeLanguage={activeLanguage}
              onChangeLanguage={setActiveLanguage}
              onTranslate={handleTranslate}
              isTranslating={isTranslating}
              hasContent={{
                en: !!sectionNameTranslations.en?.trim(),
                es: !!sectionNameTranslations.es?.trim(),
                nl: !!sectionNameTranslations.nl?.trim(),
              }}
            />

            {/* Section Name */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('mep.sectionName')}
              </label>
              <input
                type="text"
                value={sectionNameTranslations[activeLanguage] || ''}
                onChange={(e) =>
                  setSectionNameTranslations((prev) => ({ ...prev, [activeLanguage]: e.target.value }))
                }
                className={cn(
                  'w-full px-4 py-2 rounded-xl border',
                  borderPrimary,
                  'bg-white dark:bg-zinc-900',
                  'text-zinc-900 dark:text-zinc-50',
                  'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                )}
                placeholder={t('mep.sectionNamePlaceholder')}
              />
            </div>

            {/* Menu Items Selection */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-2')}>
                {t('mep.menuItems')}
              </label>

              {/* Search Input */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn(
                    'w-full pl-10 pr-4 py-2 rounded-xl border',
                    borderPrimary,
                    'bg-white dark:bg-zinc-900',
                    'text-zinc-900 dark:text-zinc-50',
                    'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                  )}
                  placeholder={t('mep.searchMenuItems') || 'Search menu items...'}
                />
              </div>

              {/* Loading State */}
              {loadingRecipes ? (
                <p className={cn(smallTextSize, secondaryText, 'text-center py-4')}>
                  {t('mep.loading')}...
                </p>
              ) : filteredMenuItems.length === 0 ? (
                <p className={cn(smallTextSize, secondaryText, 'text-center py-4')}>
                  {searchQuery
                    ? t('mep.noMenuItemsFound') || 'No menu items found'
                    : t('mep.noMenuItemsAvailable') || 'No menu items available'}
                </p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2">
                  {filteredMenuItems.map((menuItem) => {
                    const isSelected = selectedMenuItems.includes(menuItem);
                    return (
                      <div
                        key={menuItem}
                        className={cn(
                          'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition',
                          borderPrimary,
                          isSelected
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                            : 'bg-white dark:bg-zinc-900 hover:bg-gray-50 dark:hover:bg-gray-800'
                        )}
                        onClick={() => handleToggleMenuItem(menuItem)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleMenuItem(menuItem)}
                          className="w-4 h-4 rounded border-gray-300 dark:border-borderPrimary-dark text-green-600 dark:text-textGreen-dark focus:ring-green-500 dark:focus:ring-green-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className={cn(smallTextSize, isSelected ? primaryText : secondaryText)}>
                          {menuItem}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Selected Items Summary */}
              {selectedMenuItems.length > 0 && (
                <div className="mt-4">
                  <p className={cn(smallTextSize, semiBoldText, primaryText, 'mb-2')}>
                    {t('mep.selectedMenuItems')} ({selectedMenuItems.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedMenuItems.map((menuItem) => (
                      <div
                        key={menuItem}
                        className={cn(
                          'flex items-center gap-2 px-3 py-1 rounded-lg',
                          'bg-green-100 dark:bg-green-900/30',
                          'border border-green-300 dark:border-green-700'
                        )}
                      >
                        <span className={cn(smallTextSize, primaryText)}>{menuItem}</span>
                        <button
                          onClick={() => handleToggleMenuItem(menuItem)}
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          title={t('mep.removeMenuItem') || 'Remove'}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Custom Items */}
            <div>
              <label className={cn(smallTextSize, semiBoldText, primaryText, 'block mb-1')}>
                {t('mep.customItems')}
              </label>
              <p className={cn(smallTextSize, secondaryText, 'mb-2')}>
                {t('mep.customItemsDescription')}
              </p>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={customItemInput}
                  onChange={(e) => setCustomItemInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomItem();
                    }
                  }}
                  className={cn(
                    'flex-1 px-4 py-2 rounded-xl border',
                    borderPrimary,
                    'bg-white dark:bg-zinc-900',
                    'text-zinc-900 dark:text-zinc-50',
                    'focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500'
                  )}
                  placeholder={t('mep.customItemPlaceholder')}
                />
                <button
                  type="button"
                  onClick={handleAddCustomItem}
                  disabled={!customItemInput.trim()}
                  className={cn(
                    'px-4 py-2 rounded-xl',
                    'bg-darkGreenColor text-white dark:bg-darkGreenColor-dark dark:text-white',
                    'hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {t('mep.addCustomItem')}
                </button>
              </div>

              {customItems.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {customItems.map((ci) => (
                    <div
                      key={ci.id}
                      className={cn(
                        'flex items-center gap-2 px-3 py-1 rounded-lg',
                        'bg-amber-100 dark:bg-amber-900/30',
                        'border border-amber-300 dark:border-amber-700'
                      )}
                    >
                      <span className={cn(smallTextSize, primaryText)}>{ci.text}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomItem(ci.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                        title={t('mep.removeCustomItem') || 'Remove'}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 pt-6 border-t border-zinc-200 dark:border-zinc-700">
            <button
              onClick={onClose}
              disabled={saving}
              className={cn(
                'flex-1 px-6 py-3 rounded-xl',
                'border',
                borderPrimary,
                secondaryText,
                'hover:bg-gray-50 dark:hover:bg-gray-800',
                'disabled:opacity-50'
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={cn(
                'flex-1 px-6 py-3 rounded-xl',
                'bg-darkGreenColor text-white dark:bg-darkGreenColor-dark dark:text-white',
                'hover:bg-darkGreenColor/95 dark:hover:bg-darkGreenColor-dark/95',
                'disabled:opacity-50'
              )}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
